import {
  BlueskyScheduleVideoArgs,
  BlueskyShareVideoArgs,
} from 'redux/middleware/api/podcast-service';
import {
  scheduleVideo,
  shareVideo,
} from 'redux/middleware/api/podcast-service/actions';
import { getSocialProfile } from 'redux/middleware/api/third-party-authentication-service/actions';
import { GetSocialProfileAction } from 'redux/middleware/api/third-party-authentication-service/types';
import { isShareStatusResolved } from 'redux/middleware/api/video-export-service';
import { getSocialShare } from 'redux/middleware/api/video-export-service/actions';
import {
  GetSocialShareResult,
  ShareStatus,
} from 'redux/middleware/api/video-export-service/types';
import {
  onBlueskySubmitForUpload,
  onBlueskyVideoUploadedSuccessfully,
} from 'redux/modules/mixpanel';
import {
  showPostErrorNotification,
  showPostSuccessNotification,
  showScheduleSuccessNotification,
} from 'redux/modules/social/actions';
import { ThunkAction } from 'redux/types';
import { SocialSharePlatform } from 'types/common';
import { VIDEO_EXPORT_POLL_MAX_ATTEMPTS } from 'utils/constants';
import reduxPoll from 'utils/redux-poll';
import { Type } from './action-types';
import {
  blueskySelector,
  blueskySocialShareIdSelector,
  blueskySocialSharesSelector,
  blueskyUserDataSelector,
} from './selectors';
import { BlueskyAuthorizeSuccessAction, BlueskyPostMessageData } from './types';

const BLUESKY_POST_ID = 'app/bluesky/post-poll';
const BLUESKY_PROFILE_ID = 'app/bluesky/profile-poll';

export const getBlueskyUser = (
  response: GetSocialProfileAction,
): ThunkAction<Promise<void>> => async dispatch => {
  dispatch({ type: Type.BLUESKY_USER_DATA_GET_REQUEST });

  try {
    dispatch({
      payload: response,
      type: Type.BLUESKY_USER_DATA_GET_SUCCESS,
    });
  } catch (error) {
    dispatch({
      error: new Error('Error getting user data'),
      type: Type.BLUESKY_USER_DATA_GET_FAILURE,
    });

    throw error;
  }
};

export const clearBlueskyUser = () => ({
  type: Type.BLUESKY_USER_DATA_CLEAR,
});

const showSuccessNotification = (): ThunkAction<void> => (
  dispatch,
  getState,
) => {
  const { sharePostUrl } = blueskySocialSharesSelector(getState());

  dispatch(
    showPostSuccessNotification({
      sharePostUrl,
    }),
  );
};

const getShareStatus = (response: GetSocialShareResult): ShareStatus => {
  const { entities, result } = response;

  return entities?.socialShares?.[result]?.shareStatus;
};

const waitForBlueskyPost = (): ThunkAction<Promise<void>> => async (
  dispatch,
  getState,
) => {
  dispatch({ type: Type.BLUESKY_VIDEO_POST_AWAIT_REQUEST });

  const socialShareId = blueskySocialShareIdSelector(getState());

  try {
    await reduxPoll(
      dispatch,
      async () => {
        const { response } = await dispatch(getSocialShare(socialShareId));

        const shareStatus = getShareStatus(response);

        if (shareStatus === 'completed') {
          dispatch({
            type: Type.BLUESKY_VIDEO_POST_AWAIT_SUCCESS,
          });

          dispatch(showSuccessNotification());
          dispatch(onBlueskyVideoUploadedSuccessfully());
        }

        if (shareStatus === 'error') {
          dispatch({
            type: Type.BLUESKY_VIDEO_POST_AWAIT_FAILURE,
          });

          dispatch(showPostErrorNotification());
        }

        return response;
      },
      {
        id: BLUESKY_POST_ID,
        intervalMillis: 3000,
        maxAttempts: VIDEO_EXPORT_POLL_MAX_ATTEMPTS,
        shouldContinue: (error, response) => {
          const shareStatus = getShareStatus(response);

          if (error || shareStatus === 'error') {
            return false;
          }

          const share = blueskySocialSharesSelector(getState());

          return isShareStatusResolved(share, ['uploaded']);
        },
      },
    );
  } catch (err) {
    dispatch({ type: Type.BLUESKY_VIDEO_POST_AWAIT_FAILURE });

    throw err;
  }
};

export const waitForBlueskyProfile = (
  blueskyUserId?: string,
): ThunkAction<Promise<void>> => async (dispatch, getState) => {
  dispatch({ type: Type.BLUESKY_USER_DATA_GET_AWAIT_REQUEST });

  try {
    await reduxPoll(
      dispatch,
      async () => {
        const {
          providerUserId = blueskyUserId,
          idToken,
          accessToken,
          accessTokenSecret,
        } = blueskyUserDataSelector(getState());

        const { response } = await dispatch(
          getSocialProfile(
            SocialSharePlatform.BLUESKY,
            providerUserId,
            idToken,
            accessToken,
            accessTokenSecret,
          ),
        );

        if (response) {
          dispatch(getBlueskyUser(response));
        }
      },
      {
        id: BLUESKY_PROFILE_ID,
        intervalMillis: 3000,
        maxAttempts: VIDEO_EXPORT_POLL_MAX_ATTEMPTS,
        shouldContinue: error => {
          if (error) {
            return false;
          }

          const { username } = blueskySelector(getState());

          return username === undefined;
        },
      },
    );

    dispatch({
      type: Type.BLUESKY_USER_DATA_GET_AWAIT_SUCCESS,
    });
  } catch (err) {
    dispatch({ type: Type.BLUESKY_USER_DATA_GET_AWAIT_FAILURE });

    throw err;
  }
};

export const postBlueskyVideo = (
  embedVideoId: string,
  opts: Omit<BlueskyShareVideoArgs, 'platform'>,
): ThunkAction<Promise<void>> => async (dispatch, getState) => {
  dispatch({ type: Type.BLUESKY_VIDEO_POST_REQUEST });

  const { accessToken, accessTokenSecret } = blueskyUserDataSelector(
    getState(),
  );

  dispatch(onBlueskySubmitForUpload());

  try {
    const {
      response: { socialShareId },
    } = await dispatch(
      shareVideo(embedVideoId, accessToken, {
        ...opts,
        platform: SocialSharePlatform.BLUESKY,
        accessTokenSecret,
      }),
    );

    dispatch({
      payload: { socialShareId },
      type: Type.BLUESKY_VIDEO_POST_SUCCESS,
    });

    await dispatch(waitForBlueskyPost());
  } catch (err) {
    dispatch({
      error: err,
      type: Type.BLUESKY_VIDEO_POST_FAILURE,
    });
    dispatch(showPostErrorNotification());
  }
};

export const scheduleBlueskyVideo = (
  embedVideoId: string,
  opts: Omit<BlueskyScheduleVideoArgs, 'platform'>,
): ThunkAction<Promise<void>> => async (dispatch, getState) => {
  const { accessToken, accessTokenSecret } = blueskyUserDataSelector(
    getState(),
  );

  try {
    await dispatch(
      scheduleVideo(embedVideoId, accessToken, {
        platform: SocialSharePlatform.BLUESKY,
        ...opts,
        accessTokenSecret,
      }),
    );

    dispatch(showScheduleSuccessNotification(opts.scheduledForMillis));
  } catch (err) {
    dispatch(showPostErrorNotification());
  }
};

export const blueskyAuthorizeSuccess = (
  data: Pick<
    BlueskyPostMessageData['tokenInfo'],
    'accessToken' | 'accessTokenSecret' | 'idToken' | 'providerUserId'
  >,
): BlueskyAuthorizeSuccessAction => ({
  type: Type.BLUESKY_AUTHORIZE_SUCCESS,
  payload: data,
});
